import * as _ from 'lodash';
import { TableType, getTableTypeByEntityName, CalibrationSetType } from '../../../shared/enums';
import { BaseMutableModel } from '../common-models/base-mutable-model.model';
import { CalibrationUsesValModel } from './calibration-uses-val.model';
import { OverlayTypologyModel } from './overlay/overlay-typology.model';
import { OverlayType } from '../../../shared/enums/overlay-type.enum';
import ICalibrationUsesVal = ZoneIQCalibration.ICalibrationUsesVal;
import ICalibrationUsesLabel = ZoneIQCalibration.ICalibrationUsesLabel;
import IOverlay = ZoneIQCalibration.IOverlay;
import IOverlayTypology = ZoneIQCalibration.IOverlayTypology;
import IAllowedUse = ZoneIQCalibration.IAllowedUse;
import { ICalibrationUsesValModel } from 'src/app/interfaces/calibration-uses-val-model.interface';
import { ICalibrationSetModel } from 'src/app/interfaces/calibration-set-model.interface';
import { IOverlayTypologyModel } from 'src/app/interfaces/overlay-typology-model.interface';
import { ICalibrationGeneralModel } from 'src/app/interfaces/calibration-general-model.interface';
import { ICalibrationBonusModel } from 'src/app/interfaces/calibration-bonus-model.interface';
import { ICalibrationOfficeCommercialModel } from 'src/app/interfaces/calibration-office-commercial-model.interface';
import { ICalibrationParkingModel } from 'src/app/interfaces/calibration-parking-model.interface';
import ICalibrationSetAttributesStatistic = ZoneIQCalibration.ICalibrationSetAttributesStatistic;
import { IAttributesIdentifiers } from 'src/app/interfaces/attributes-identifiers.interface';

export abstract class CalibrationSetModel<T> extends BaseMutableModel<T> implements ICalibrationSetModel<T> {
    Id: number;
    CalibrationUsesVals?: ICalibrationUsesValModel[];
    CalibrationGeneral?: ICalibrationGeneralModel;
    CalibrationBonus?: ICalibrationBonusModel;
    CalibrationParking?: ICalibrationParkingModel;
    CalibrationOfficeCommercial?: ICalibrationOfficeCommercialModel;
    OverlayTypology?: IOverlayTypologyModel[];
    isValidBuildingTypology: boolean;
    Name: string;

    CloneId: number;
    RevisionId: number;
    hasComments: boolean = false;
    hasLinks: boolean = false;
    hasAssumptions: boolean = false;
    identifier: IAttributesIdentifiers;
    protected _calibrationSetType: CalibrationSetType;
    usesLabels: ICalibrationUsesLabel[];

    constructor(data: any, fieldSettings?: any) {
        super(data, ['Id'], fieldSettings);
        this.initIdentifier();
    }

    get isCalibrationSet(): boolean {
        return true;
    }

    get isZoneCombination() {
        return this.calibrationSetType === CalibrationSetType.ZONE_COMBINATION;
    }

    get calibrationSetType(): CalibrationSetType {
        return this._calibrationSetType;
    }

    get validateRequiredFields() {
        return false;
    }

    get hasDescription(): boolean {
        return !!(this.currentState as any).Description;
    }

    static sortingCalibrationByName(a, b): number {
        return a.Name < b.Name ? -1 : a.Name > b.Name ? 1 : 0;
    }

    updateData(updatedValue: any, entityName: string, notAllowedUseId?: number, toDelete = false) {
        const tableType = getTableTypeByEntityName(entityName);
        switch (tableType) {
            case TableType.CALIBRATION_USES:
                if (toDelete) {
                    this.deleteUseValue(updatedValue, notAllowedUseId);
                } else {
                    this.updateUses(updatedValue);
                }
                return;
            case TableType.VIRTUAL_BUILDING_TYPOLOGY: // also added like stub while calibration set and zoneCombination have difference
                this.updateValues(updatedValue);
                return;
            case TableType.OVERLAY_TYPOLOGY:
                this.updateOverlayTypology(updatedValue);
                return;
            default:
                this[entityName].updateValues(updatedValue);
                return;
        }
    }

    updateOverlayTypology(updatedTypology: IOverlayTypology) {
        const existedTypologyIndex = this.OverlayTypology.findIndex(
            typology => typology.currentState.TypologyId === updatedTypology.TypologyId
        );
        if (existedTypologyIndex >= 0) {
            this.OverlayTypology[existedTypologyIndex].updateValues(updatedTypology);
            return;
        }
    }

    updateUses(updatedUseValue: ICalibrationUsesVal) {
        const existedUseIndex = this.CalibrationUsesVals.findIndex(
            use => use.currentState.CalibrationUsesLabelId === updatedUseValue.CalibrationUsesLabelId
        );
        if (existedUseIndex >= 0) {
            this.CalibrationUsesVals[existedUseIndex].updateValues(updatedUseValue);
            return;
        }
        const usesLabel = this.usesLabels.find(label => label.Id === updatedUseValue.CalibrationUsesLabelId);
        const newUseValue = new CalibrationUsesValModel(updatedUseValue, usesLabel);
        this.CalibrationUsesVals.push(newUseValue);
    }

    initCalibrationUsesVals(
        usesValsData: ICalibrationUsesVal[],
        usesLabels: ICalibrationUsesLabel[],
        allowedUses: IAllowedUse[],
        notAllowedUseId?: number,
        keyField?: string
    ) {
        if (_.isEmpty(usesLabels)) {
            return;
        }
        this.usesLabels = usesLabels;
        this.CalibrationUsesVals = [];
        usesLabels.forEach(label => {
            let useData: ICalibrationUsesVal = usesValsData.find(e => e.CalibrationUsesLabelId === label.Id);

            if (!useData && _.isNumber(notAllowedUseId)) {
                useData = {
                    AllowedUsesId: notAllowedUseId,
                    CalibrationUsesLabelId: label.Id,
                    UseLimit: null,
                    ParkingRequired: null
                };
                useData[keyField] = this.Id;
            }

            const allowedUse = allowedUses.find(au => au.Id === useData.AllowedUsesId);

            this.CalibrationUsesVals.push(new CalibrationUsesValModel(useData, label, allowedUse));
        });
    }

    initOverlayTypology(
        overlayTypologies: IOverlayTypology[],
        overlayArr: IOverlay[],
        clbSetAttrStat?: ICalibrationSetAttributesStatistic[]
    ): IOverlayTypologyModel[] {
        const typologiesArr: IOverlayTypologyModel[] = [];
        if (_.isEmpty(overlayTypologies)){
            return typologiesArr;
        }
        overlayArr.forEach(overlay => {
            if (overlay.BuildingCalibrationType === OverlayType.BUILDING_TYPOLOGY) {
                const index = overlayTypologies.findIndex(overTypo => overTypo.TypologyId === overlay.Id);
                let item;
                if (index < 0) {
                    item = {
                        Id: null,
                        Name: overlay.Name,
                        TypologyId: overlay.Id,
                        OverlayId: null,
                        IsTypologyEnabledForAllOverlays: null,
                        IsEnabled: null,
                        IsAddedToList: null,
                        IsDefault: null
                    };
                } else {
                    item = overlayTypologies[index];
                }
                const overlayTypology = new OverlayTypologyModel(item);
                if (_.get(clbSetAttrStat, 'length')) {
                    const attrStat = clbSetAttrStat.find(
                        stat =>
                            stat.ParentId === overlayTypology.currentState.Id &&
                            stat.ParentType === CalibrationSetType.OVERLAY_TYPOLOGY
                    );
                    overlayTypology.hasComments = _.get(attrStat, 'hasComments', false);
                    overlayTypology.hasLinks = _.get(attrStat, 'hasLinks', false);
                    overlayTypology.hasAssumptions = _.get(attrStat, 'hasAssumptions', false);
                }
                typologiesArr.push(overlayTypology);
            }
        });
        return typologiesArr;
    }

    deleteUseValue(useToDelete: ICalibrationUsesVal, notAllowedUseId?: number, keyField?: string) {
        if (!_.isNumber(notAllowedUseId)) {
            return;
        }
        const existedUseIndex = this.CalibrationUsesVals.findIndex(
            use => use.currentState.CalibrationUsesLabelId === useToDelete.CalibrationUsesLabelId
        );
        if (existedUseIndex >= 0) {
            const useData = {
                AllowedUsesId: notAllowedUseId,
                CalibrationUsesLabelId: useToDelete.CalibrationUsesLabelId,
                UseLimit: null,
                ParkingRequired: null
            };
            useData[keyField] = this.Id;
            this.CalibrationUsesVals[existedUseIndex].setDefault(useData);
        }
    }

    cancelChanges() {
        if (this.OverlayTypology) {
            this.OverlayTypology.forEach((overTypo: IOverlayTypologyModel) => {
                if (overTypo.isChanged) {
                    overTypo.removeChanges();
                }
            });
        }

        if (this.CalibrationGeneral.isChanged) {
            this.CalibrationGeneral.removeChanges();
        }
        if (this.CalibrationParking.isChanged) {
            this.CalibrationParking.removeChanges();
        }
        if (this.CalibrationOfficeCommercial.isChanged) {
            this.CalibrationOfficeCommercial.removeChanges();
        }
        if (this.CalibrationBonus.isChanged) {
            this.CalibrationBonus.removeChanges();
        }
        this.CalibrationUsesVals.forEach((usesVal: ICalibrationUsesValModel) => {
            if (usesVal.isChanged) {
                usesVal.removeChanges();
            }
        });
    }

    cloneCalibrationUsesValues(usesValues: ICalibrationUsesValModel[]) {
        if (!usesValues) {
            return;
        }

        usesValues.forEach((sourceUsesValue: ICalibrationUsesValModel) => {
            const currentUsesValue = this.CalibrationUsesVals.find(
                usesValue =>
                    usesValue.currentState.CalibrationUsesLabelId ===
                    sourceUsesValue.currentState.CalibrationUsesLabelId
            );
            if (!currentUsesValue) {
                console.log(
                    `CalibrationSetModel.cloneCalibrationUsesValues => found not matching use value for CalibrationUsesLabelId: ${sourceUsesValue.CalibrationUsesLabelId}`
                );
            } else {
                currentUsesValue.currentState.AllowedUsesId = sourceUsesValue.currentState.AllowedUsesId;
                currentUsesValue.currentState.UseLimit = sourceUsesValue.currentState.UseLimit;
                currentUsesValue.currentState.ParkingRequired = sourceUsesValue.currentState.ParkingRequired;
            }
        });
    }

    initIdentifier() {
        this.identifier = {
            calibrationSetId: (this.currentState as any).Id,
            calibrationSetType: this.calibrationSetType
        };
    }
}
